const SystemFallbackLanguage = 'en';
const DefaultLandingPageId = 1;
const PasswordPattern = /^(?=.*\d)(?=.*[A-Z])(?=.*[a-z])(?=.*[^\w\d\s:])([^\s]){8,}$/;

const AuthModes = Object.freeze({
    DEFAULT: 0,
    SAML: 1,
    LDAP: 2
});

const RiskGroup = Object.freeze({
    Unknown: 0,
    Serial: 1,
    Frequent: 2,
    Rare: 3,
    Defender: 4
    // do not use -99, already used as placeholder
});

const Status = Object.freeze({
    Active: 'active',
    Pending: 'pending',
    RegisterError: 'register error',
    Suspended: 'suspended'
});

const LicenseStatus = Object.freeze({
    Active: 'active',
    Pending: 'pending',
    RegisterError: 'register error',
    Suspended: 'suspended',
    Grace: 'grace',
    Inactive: 'inactive',
});

const Campaign = {
    Status: Object.freeze({
        PendingApproval: 1,
        Approved: 2,
        Starting: 3,
        Running: 4,
        Completed: 5,
        Archived: 6
    }),
    Type: Object.freeze({
        Ordinary: 1,
        Test: 2,
        RemediationWeak: 3,
        RemediationWorsen: 4,
        RemediationClickers: 5,
        RemediationLight: 6,
        RemediationAdaptive: 7,
        RemediationQRCode: 8,
        RemediationUSB: 9,
        RemediationMedia: 11,
        All: 99
    })
};

const remFilters = Object.freeze({
    Site: null,
    Department: null,
    click_rate: {
        min: 0,
        max: 100,
    },
    target_risk: {
        [RiskGroup.Serial]: null,
        [RiskGroup.Frequent]: null,
        [RiskGroup.Rare]: null,
        [RiskGroup.Defender]: null,
    }
});

const ServiceType = Object.freeze({
    Awareness: 1,
    Phishing: 2,
    Channel: 3,
    Remediation: 4,
    CyberAdvisor: 5,
});

const kpiServiceAssociation = Object.freeze({
    active_defense: ServiceType.Phishing,
    behaviour: ServiceType.Phishing,
    learning: ServiceType.Awareness,
    participation: ServiceType.Awareness,
    update: ServiceType.Channel,
});

const cgaTypeDefImages = Object.freeze({
    1: document.location.origin + require('../assets/modules/awareness.jpg'),
    2: document.location.origin + require('../assets/modules/channel.jpg'),
    3: document.location.origin + require('../assets/modules/pills.jpg')
});

const campaignDefImage = document.location.origin + require('../assets/campaigns/campaign.jpg');

const TemplateType = Object.freeze({
    Undefined: 0,
    Email: 1,
    EmailQRCode: 3,
    SMS: 2,
    QRCode: 8,
    USB: 9,
    Media: 11
});

const TemplateFilter = Object.freeze({
    Included: '1',
    NotIncluded: '0',
    All: ''
});

const EventType = Object.freeze({
    ContentCGA: 'content_cga',
    ContentCGC: 'content_cgc',
    ContentCGR: 'content_cgr',
    FirstLicenseActivation: 'first_license_activation',
    MultipleContent: 'multiple_content',
    FirstContentCGAOrCGC: 'first_content_cga_or_cgc',
    NotifyCgaUsersNotAlignedWithPaths: 'notify_cga_users_not_aligned_with_paths',
    NotifyCgcUsersNotAlignedWithPaths: 'notify_cgc_users_not_aligned_with_paths',
    InternalEvent: 'internal_event',
    NotifyInactiveStudents: 'notify_all_inactive_students',
    NotifyRegularStudents: 'notify_all_regular_students',
    NotifyIrregularStudents: 'notify_all_irregular_students',
    NotifyOnlineStudents: 'notify_all_online_students',
});

const ScheduleModes = Object.freeze({
    Instant: 'instant',
    Date: 'date',

});

const TemplateScope = Object.freeze({
    Company: 0,
    Global: 1,
    GlobalAndCompany: 2
});

const TemplateTestedStatus = Object.freeze({
    Ok: 1,
    Pending: 0,
    Fail: -1
});

const LandingPageScope = Object.freeze({
    Company: 0,
    Global: 1,
    GlobalAndCompany: 2
});

const TablePaginationSize = [
    { value: 10, text: 10 },
    { value: 15, text: 15 },
    { value: 25, text: 25 },
    { value: 50, text: 50 },
    { value: 100, text: 100 }
];

const LicenseAccountStatus = Object.freeze({
    Licensed: 'licensed',
    Unlicensed: 'unlicensed'
});

const TargetMode = Object.freeze({
    All: {
        value: 1,
        label: 'StudentCaring.EventTargetMode.all'
    },
    NotActiveUsers: {
        value: 2,
        label: 'StudentCaring.EventTargetMode.notActive'
    },
    NotRegularUsers: {
        value: 3,
        label: 'StudentCaring.EventTargetMode.notRegular'
    },
    OnlineUsers: {
        value: 4,
        label: 'StudentCaring.EventTargetMode.online'
    },
    RegularUsers: {
        value: 5,
        label: 'StudentCaring.EventTargetMode.regular'
    }
});

const Roles = Object.freeze({
    Admin: 'admin',
    ContentManager: 'contentManager',
    CSM: 'csm',
    MSP: 'msp',
    CompanyAdmin: 'companyAdmin',
    Supervisor: 'supervisor',
    Teamleader: 'teamleader',
    Student: 'studente',
});

const RolesScale = Object.freeze([
    Roles.Admin,
    Roles.ContentManager,
    Roles.CSM,
    Roles.MSP,
    Roles.CompanyAdmin,
    Roles.Supervisor,
    Roles.Teamleader,
    Roles.Student
]);

const RolesBaseRoute = Object.freeze({
    admin: '/admin',
    contentManager: '/cm',
    csm: '/csm',
    msp: '/msp',
    companyAdmin: '/companyadmin',
    supervisor: '/supervisor',
    teamleader: '/teamleader',
    studente: '/student'
});

const Permission = Object.freeze({
    account: {
        create: 'create-account',
        delete: 'delete-account',
        modify: 'modify-account',
        modify_profile: 'edit-profile',
        modify_pwd: 'change-password',
        view: 'view-account',
        list: 'list-account'
    },
    role: {
        create: 'create-role',
        delete: 'delete-role',
        modify: 'modify-role',
        view: 'view-role',
        list: 'list-role'
    },
    company: {
        create: 'create-company',
        view: 'view-company',
        delete: 'delete-company',
        modify: 'modify-company',
        modify_labels: 'modify-company-labels',
        modify_smtp: 'modify-company-smtp',
        modify_campaign: 'modify-company-campaign',
        modify_email_domain: 'modify-company-email-domain',
        modify_logo: 'modify-company-logo',
        list: 'list-company',
        list_all: 'list-company-all',
        create_report: 'create-company-report',
        export_data: 'export-company-data',
        override_org_1: 'override-filter-org_1',
        override_org_2: 'override-filter-org_2',
        override_org_3: 'override-filter-org_3',
        override_org_4: 'override-filter-org_4'
    },
    campaign: {
        approve: 'approve-campaign',
        delete: 'delete-campaign',
        view: 'view-campaign',
        modify: 'modify-campaign',
        view_summary: 'view-campaign-summary',
        view_templates: 'view-campaign-templates',
        view_targets: 'view-campaign-targets',
        view_remediation: 'view-remediation'
    },
    target: {
        view: 'view-target',
        create: 'create-target',
        modify: 'modify-target',
        delete: 'delete-target',
        import: 'import-target',
        details: 'view-target-details'
    },
    template: {
        view: 'view-template',
        create: 'create-template',
        delete: 'delete-template',
        modify: 'modify-template',
        list: 'list-template',
        test: 'test-template',
        enable: 'enable-campaign-template',
        view_hint: 'view-template-hint',
        modify_hint: 'modify-template-hint',
        delete_hint: 'delete-template-hint',
        create_hint: 'create-template-hint'
    },
    landing_page: {
        view: 'view-landing-page',
        create: 'create-landing-page',
        modify: 'modify-landing-page',
        delete: 'delete-landing-page'
    }
});

const ModuleStatuses = Object.freeze({
    InProgress: 0,
    Available: 1,
    Blocked: 2,
    Completed: 3,
    Propedeutic: 4,
    NotAvailable: 5
});

const ModuleCompletionStatuses = Object.freeze({
    Incomplete: 0,
    Failed: 1,
    Passed: 2
});

const BadgeModuleStatuses = Object.freeze({
    NotAvailable: 0,
    NotEarned: 1,
    Earned: 2
});

const CgaStatusType = Object.freeze({
    NOTACTIVE: 1,
    ONLINE: 2,
    REGULAR: 3,
    NOTREGULAR: 4
});

const ReleaseTypes = Object.freeze({
    CustomRelease: 'custom_release',
    FirstDayMonth: 'first_day_month',
    FirstMondayMonth: 'first_monday_month'
});

const RemediationType = Object.freeze({
    AdaptiveRemediation: 'adaptive_remediation'
});

const VideoMode = Object.freeze({
    FREE: 1,
    LOCKED: 2
});

const LicenseMaxQuantity = 999999;

const NotAssignedLabel = "not_assigned_label";

const LandingPageHtml =
    `<!DOCTYPE html>
<html lang="{{.landingPageLanguage}}">
    <head>
        <meta charset="UTF-8">
        <title>{{.landingPageTitle}}</title>
        <style>
            body { font-family: Arial, Helvetica, sans-serif; }
            h1 { color: #13598d; text-align: center; font-size: 1em; } 
            div { text-align: center; margin: 10% auto; width: max-content; }
        </style>
    </head>
    <body>
        <div>
            <h1>{{.landingPageBody}}</h1>
        </div>
    </body>
</html>`;

const ErrorCodes = Object.freeze({
    AWARENESS: {
        LEVEL_MODULE_GENERIC_ERROR: "error.awareness.cga_level_module.generic_error",
        SWITCH_MODULES: {
            ASSOCIATION_NOT_FOUND: "error.awareness.cga_level_module.association_not_found",
        },
        DELETE_CGA_LEVEL_MODULE: {
            PATH_OR_MODULE_NOT_FOUND: "error.awareness.cga_level_module.delete_cga_level_module_module_or_path_not_found",
            LEVEL_ALREADY_ASSIGNED: "error.awareness.cga_level_module.level_assigned"
        }
    }
});

const OptionDataType = Object.freeze({
    0: 'string',
    1: 'integer',
    2: 'float',
    3: 'boolean'
});

const FormDataType = Object.freeze({
    0: 'checkbox',
    1: 'select',
    2: 'input',
    3: 'textarea',
    4: 'radio',
    5: 'password'
});

const Base64EncodedOptions = Object.freeze(['smtp_password', 'google_private_key']);

const ChartDesiredTitleLength = 25;

const ChartMaxSliceSize = 9;

const QRCode = Object.freeze({
    value: `data:image/png;base64,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`
});

const CompanyStatus = Object.freeze({
    0: 'Setup',
    1: 'GoLive',
    2: 'Running',
    3: 'Suspended',
    4: 'Archived',
    5: 'Deleted',
});

const ScimStatus = Object.freeze({
    Active: 'Active',
    Pending: 'Pending',
    NotActive: 'NotActive',
});


const CompanyStatusValues = Object.freeze({
    SETUP: 0,
    GOLIVE: 1,
    RUNNING: 2,
    SUSPENDED: 3,
    ARCHIVED: 4,
    DELETED: 5,
});

const MaxCampaignReport = 12;

const EmailLimit = 500;

const EmailTemplate =
    `
<!DOCTYPE html>
<html style="overflow-x: hidden; margin: 0px; padding: 0px; width: 100%; height: 100%">
  <head></head>
  <body topmargin="0" rightmargin="0" bottommargin="0" leftmargin="0" marginwidth="0" marginheight="0" width="100%" style="border-collapse: collapse; border-spacing: 0; margin: 0px; padding: 0px; width: 100%; height: 100%; line-height: 100%; color: rgb(0, 0, 0); overflow-x: hidden; background-color: rgb(240, 240, 240)">
    <table width="100%" align="center" border="0" cellpadding="0" cellspacing="0" style="border-collapse: collapse; border-spacing: 0; padding: 0px; width: 100%; margin-bottom: 60px; line-height: 150%; margin-top: 30px" class="background">
      <tbody>
        <tr>
          <td class="sfondo" align="center" valign="top" style="border-collapse: collapse; border-spacing: 0; margin: 0px; padding: 0px; line-height: 150%">
            <table id="logos" border="0" cellpadding="0" cellspacing="0" align="center" width="650" style="border-collapse: collapse; border-spacing: 0; padding: 0px; max-width: 650px; line-height: 150%; margin: 0px; background-color: rgb(255, 255, 255); border-radius: 8px 8px 0 0" class="wrapper">
              <tbody>
                <tr>
                  <td align="left" valign="middle" style="border-collapse: collapse; border-spacing: 0; margin: 0px; padding: 30px 0px 5px 30px; width: 325px; line-height: 150%"><img id="logoCyber" border="0" vspace="0" hspace="0" src="{{created_by_company_logo}}" height="60" alt="{{created_by_company_displayname}}" title="{{created_by_company_displayname}}" style="color: rgb(0, 0, 0); font-size: 10px; margin: 0px; padding: 0px; outline: none; text-decoration: none; display: block; border: 0px; line-height: 100%; height: 60px" /></td>
                  <td align="right" valign="middle" style="border-collapse: collapse; border-spacing: 0; margin: 0px; padding: 30px 30px 5px 0px; width: 325px; line-height: 150%"><img id="logoCompany" height="40" border="0" vspace="0" hspace="0" src="{{company_logo_url}}" alt="{{company_name}}" title="{{company_name}}" style="color: rgb(0, 0, 0); font-size: 10px; margin: 0px; padding: 0px; outline: none; text-decoration: none; border: 0px; line-height: 100%; display: block; height: 40px" /></td>
                </tr>
              </tbody>
            </table>
            <table border="0" cellpadding="0" cellspacing="0" align="center" width="650" style="background-color: rgb(255, 255, 255); border-collapse: collapse; border-spacing: 0; padding: 0px; max-width: 650px; line-height: 150%" class="container">
              <tbody>
                <tr>
                  <td align="" valign="top" style="border-collapse: collapse; border-spacing: 0; margin: 0; padding-left: 30px; padding-right: 30px; width: 87.5%; font-size: 17px; font-weight: 400; line-height: 160%; padding-top: 0; padding-bottom: 20px; color: #000000; font-family: sans-serif" class="paragraph">{{mail_content}}</td>
                </tr>
                <tr>
                  <td align="" valign="top" style="border-collapse: collapse; border-spacing: 0; margin: 0; padding-left: 30px; padding-right: 30px; width: 87.5%; font-size: 17px; font-weight: 400; line-height: 160%; padding-top: 0; padding-bottom: 20px; color: #000000; font-family: sans-serif" class="paragraph"></td>
                </tr>
              </tbody>
            </table>
          </td>
        </tr>
      </tbody>
    </table>
  </body>
</html>
`

const CyberGuru = Object.freeze({
    Name: 'Cyber Guru',
    LegalName: 'Cyber Guru S.p.A.',
    VAT: '14324511006',
    RegisteredOffice: 'Viale della Grande Muraglia, 284 - 00144 Rome',
    Logo: 'https://cdn.cyberguru.it/assets/logoCGmail.png',
    SupportEmail: 'support@libracyber.com',
    NoreplyEmail: 'no-reply@cyberguru.eu',
    SupportPortalUrl: 'https://support.libracyber.com/',
    // CG20-6150: the historical knowledge base, used only until the cutover. From that date the
    // knowledge base is published on the support portal and SupportPortalUrl takes over.
    KnowledgeBaseUrl: 'https://support.cyberguru.eu/hc/'
});

// CG20-6126: indirizzi di supporto che sono DEFAULT DI PIATTAFORMA (seedati nel DB,
// migrati, o scritti dal fallback del form admin), NON una vera personalizzazione della
// company. Un support_address non-vuoto da solo NON significa "personalizzato": va escluso
// se coincide con uno di questi. Confronto trimmed/case-insensitive. Tenere allineato a
// CyberGuru.SupportEmail e al valore della migration backend.
const PlatformDefaultSupportEmails = Object.freeze([
    'support@cyberguru.eu',
    'support@libracyber.com',
    CyberGuru.SupportEmail,
].map(e => e.toLowerCase()));

// CG20-6143: the portal only becomes the support channel at the cutover; until then the
// historical address stands, which is what users saw before the change. Restoring the old
// code is not enough: it read the stored value, and the migration has since rewritten that
// to a platform default. Keep the date aligned with SUPPORT_PORTAL_CUTOVER_DATE in email_proxy.
const PreCutoverSupportEmail = 'support@cyberguru.eu';
const SupportPortalCutover = new Date('2026-08-18T00:00:00+02:00');

const LicenseMonthsAvailabilityCreation = [
    { value: 12, label: '12', roles: ['admin', 'csm', 'msp'] },
    { value: 13, label: '13', roles: ['admin', 'csm'] },
    { value: 24, label: '24', roles: ['admin', 'csm', 'msp'] },
    { value: 25, label: '25', roles: ['admin', 'csm'] },
    { value: 36, label: '36', roles: ['admin', 'csm', 'msp'] },
    { value: 37, label: '37', roles: ['admin', 'csm'] },
];

const LicenseMonthsAvailabilityExtend = [
    { value: 12, label: '12', roles: ['admin', 'csm', 'msp'] },
    { value: 24, label: '24', roles: ['admin', 'csm', 'msp'] },
    { value: 36, label: '36', roles: ['admin', 'csm', 'msp'] },
];

const LicenseActions = Object.freeze({
    ADD_PACKAGE: 'add_package',
    EXTEND_LICENSE: 'extend_license',
    ADD_QUANTITY: 'add_quantity',
})

const CgaContentType = Object.freeze({
    NOT_ADDITIONAL: 0,
    ADDITIONAL: 1
})

const CompanyReleaseStatus = Object.freeze({
    Created: 'created',
    Queued: 'queued',
    Started: 'started',
    Completed: 'completed',
    Failed: 'failed'
})

const AdditionalContentService = Object.freeze({
    name: 'Additional contents',
    value: "0",
    additional: 1,
    urlName: 'additional-contents',
    show: true
})


const CgaTypeBoardValues = Object.freeze({
    0: 'Standard',
    1: 'NIS2',
    2: 'NIS2PA',
    3: 'DORA'
})

const CgaTypeBoardStateMap = Object.freeze({
    'NIS2': 'success',
    'NIS2PA': 'info',
    'DORA': 'warning'
})

const AlertsType = Object.freeze({
    'License': 'licenses',
    'Campaign': 'campaigns',
    'Release': 'releases'
})

const LevelStatus = Object.freeze({
    not_deprecated: 0,
    deprecated: 1
})

const DownloadableFileStatus = Object.freeze({
    error: 0,
    pending: 1,
    ready: 2,
    expired: 3
});

const DownloadableFileType = Object.freeze({
    certificate: 1,
    report: 2,
});

const DownloadableFileExpirationThresholds = Object.freeze({
    days: 7,
    weeks: 2,
    months: 1,
    years: 1
});
const messageType = Object.freeze({
    welcome: '/welcome_message',
    search: '/search ',
    sent: 'sent',
    received: 'received'
})

const IPListType = Object.freeze({
    whitelist: 'whitelist',
    blacklist: 'blacklist',
    robot_ips: 'robot_ips'
})

const ProvisioningType = Object.freeze({
    Manual: 'Manual',
    Automatic: 'Automatic'
})

const AuthenticationType = Object.freeze({
    SSO: 'SSO',
    Credentials: 'Credentials',
    Hybrid: 'Hybrid'
})

const LicenseType = Object.freeze({
    Awareness: 'Awareness',
    Channel: 'Channel',
    Phishing: 'Phishing',
    PhishPRO: 'Phish PRO',
    CA: 'Cyber Advisor'
})

const LicenseAssignmentThreshold = Object.freeze({
    users: 2500
})

const OperationType = Object.freeze({
    IMPORT_ACCOUNT: 'IMPORT_ACCOUNT',
});
const atlassianServiceStatusUrl = "https://cyberguru1.statuspage.io/embed/script.js"

const EmailLogTypes = Object.freeze([
    { value: 'system', label: 'System Email Logs' },
    { value: 'student_caring', label: 'Student Caring Email Logs' }
]);

const EmailCategoryTypes = Object.freeze([
    { value: 0, label: 'All' },
    { value: 3, label: 'Awareness - User Not Aligned' },
    { value: 6, label: 'Channel - User Not Aligned' },
    { value: 5, label: 'Content CG Awareness' },
    { value: 7, label: 'Content CG Channel' },
    { value: 11, label: 'First Content Channel' },
    { value: 1, label: 'First Content Awareness' },
    { value: 4, label: 'First License Activation' },
    { value: 2, label: 'Multiple Content' },
    { value: 10, label: 'Password Reset' },
    { value: 9, label: 'Phishing Notification' },
    { value: 8, label: 'Users Status' }
]);

const SummaryAction = Object.freeze({
    Clicked: "clicked",
    Reported: "reported"
});

const SmtpKeys = ['smtp_host', 'smtp_port', 'smtp_username', 'smtp_password'];


const CalenderBlockingPeriod = 7; // number of days to block the calendar for next execution when the event has been executed (for notify events)

// CG20-6122 - codici del gatekeeper CDN backend (mirror di server/common/cdnGatekeeper.js)
const CdnCode = Object.freeze({
    Blocked: 'ER_EXTERNAL_CDN_BLOCKED',
    Warning: 'WARN_EXTERNAL_CDN'
});


exports.ChartDesiredTitleLength = ChartDesiredTitleLength;
exports.ChartMaxSliceSize = ChartMaxSliceSize;
exports.ModuleStatuses = ModuleStatuses;
exports.ModuleCompletionStatuses = ModuleCompletionStatuses;
exports.BadgeModuleStatuses = BadgeModuleStatuses;
exports.ServiceType = ServiceType;
exports.Campaign = Campaign;
exports.Status = Status;
exports.LicenseStatus = LicenseStatus;
exports.DefaultLandingPageId = DefaultLandingPageId;
exports.RiskGroup = RiskGroup;
exports.cgaTypeDefImages = cgaTypeDefImages;
exports.campaignDefImage = campaignDefImage;
exports.kpiServiceAssociation = kpiServiceAssociation;
exports.TemplateType = TemplateType;
exports.EventType = EventType;
exports.ScheduleModes = ScheduleModes;
exports.TemplateScope = TemplateScope;
exports.TemplateTestedStatus = TemplateTestedStatus;
exports.TablePaginationSize = TablePaginationSize;
exports.LandingPageScope = LandingPageScope;
exports.Roles = Roles;
exports.RolesScale = RolesScale;
exports.RolesBaseRoute = RolesBaseRoute;
exports.Perm = Permission;
exports.NotAssignedLabel = NotAssignedLabel;
exports.LandingPageHtml = LandingPageHtml;
exports.CgaStatusType = CgaStatusType;
exports.VideoMode = VideoMode;
exports.ErrorCodes = ErrorCodes;
exports.OptionDataType = OptionDataType;
exports.FormDataType = FormDataType;
exports.Base64EncodedOptions = Base64EncodedOptions;
exports.TargetMode = TargetMode;
exports.PasswordPattern = PasswordPattern;
exports.QRCode = QRCode;
exports.TemplateFilter = TemplateFilter;
exports.CompanyStatus = CompanyStatus;
exports.ScimStatus = ScimStatus;
exports.ReleaseTypes = ReleaseTypes;
exports.RemediationType = RemediationType;
exports.CompanyStatusValues = CompanyStatusValues;
exports.AuthModes = AuthModes;
exports.MaxCampaignReport = MaxCampaignReport
exports.EmailLimit = EmailLimit;
exports.CyberGuru = CyberGuru;
exports.PlatformDefaultSupportEmails = PlatformDefaultSupportEmails;
exports.PreCutoverSupportEmail = PreCutoverSupportEmail;
exports.SupportPortalCutover = SupportPortalCutover;
exports.EmailTemplate = EmailTemplate;
exports.LicenseMonthsAvailabilityCreation = LicenseMonthsAvailabilityCreation;
exports.LicenseMonthsAvailabilityExtend = LicenseMonthsAvailabilityExtend;
exports.CgaContentType = CgaContentType;
exports.CompanyReleaseStatus = CompanyReleaseStatus;
exports.AdditionalContentService = AdditionalContentService;
exports.AlertsType = AlertsType;
exports.LevelStatus = LevelStatus;
exports.remFilters = remFilters;
exports.LicenseAccountStatus = LicenseAccountStatus;
exports.messageType = messageType;
exports.IPListType = IPListType;
exports.LicenseType = LicenseType;
exports.LicenseMaxQuantity = LicenseMaxQuantity;
exports.ProvisioningType = ProvisioningType;
exports.AuthenticationType = AuthenticationType;
exports.LicenseActions = LicenseActions;
exports.OperationType = OperationType;
exports.atlassianServiceStatusUrl = atlassianServiceStatusUrl;
exports.DownloadableFileStatus = DownloadableFileStatus;
exports.DownloadableFileType = DownloadableFileType;
exports.DownloadableFileExpirationThresholds = DownloadableFileExpirationThresholds;
exports.LicenseAssignmentThreshold = LicenseAssignmentThreshold
exports.CgaTypeBoardValues = CgaTypeBoardValues;
exports.CgaTypeBoardStateMap = CgaTypeBoardStateMap;
exports.EmailLogTypes = EmailLogTypes;
exports.EmailCategoryTypes = EmailCategoryTypes;
exports.SummaryAction = SummaryAction;
exports.SmtpKeys = SmtpKeys;
exports.CalenderBlockingPeriod = CalenderBlockingPeriod;
exports.SystemFallbackLanguage = SystemFallbackLanguage;
exports.CdnCode = CdnCode;